import PrimaryButton from '@/components/Buttons/PrimaryButton';
import SelectInput from '@/components/Forms/SelectInput';
import TextInput from '@/components/Forms/TextInput';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { useForm } from '@inertiajs/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface EnrollmentCertificateProps {
    enrolled_courses: { id: number; title: string }[];
    errors?: {
        course_id?: string;
        dni?: string;
        address?: string;
        non_field_errors?: string[];
    };
    csrfToken?: string;
}

const EnrollmentCertificateContent: React.FC<EnrollmentCertificateProps> = ({
    enrolled_courses,
    errors: serverErrors,
    csrfToken,
}) => {
    const { t } = useTranslation();

    // Using useForm just for form state management, not for submission
    const { data, setData, errors } = useForm<{
        course_id: string;
        dni: string;
        address: string;
    }>({
        course_id: '',
        dni: '',
        address: '',
    });

    const hasCourses = enrolled_courses.length > 0;

    return (
        <AppLayout>
            <PageTitle title="Certificado de Matriculacion" />
            <div className="mx-auto max-w-3xl space-y-8">
                {/* Page Header */}
                <div className="space-y-4">
                    <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl dark:text-white">
                        {t('enrollmentCertificate.title')}
                    </h1>
                    <p className="text-lg text-gray-700 sm:text-xl dark:text-neutral-300">
                        {t('enrollmentCertificate.description')}
                    </p>
                </div>

                {/* Form Card */}
                <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8 dark:border-neutral-700 dark:bg-neutral-800">
                    {serverErrors?.non_field_errors && (
                        <div className="mb-6 rounded-lg bg-red-50 p-4 text-red-800">
                            {serverErrors.non_field_errors.join(', ')}
                        </div>
                    )}

                    <form
                        method="POST"
                        action="/enrollment-certificate/download/"
                        className="space-y-6"
                    >
                        <input
                            type="hidden"
                            name="csrfmiddlewaretoken"
                            value={csrfToken}
                        />
                        {/* Course Field */}
                        <SelectInput
                            label={t('enrollmentCertificate.courseLabel')}
                            name="course_id"
                            value={data.course_id}
                            onChange={(v) => setData('course_id', v)}
                            disabled={!hasCourses}
                            error={errors.course_id || serverErrors?.course_id}
                        >
                            <option value="">
                                {hasCourses
                                    ? t('enrollmentCertificate.selectCourse')
                                    : t(
                                          'enrollmentCertificate.noCoursesAvailable'
                                      )}
                            </option>
                            {enrolled_courses.map((course) => (
                                <option key={course.id} value={course.id}>
                                    {course.title}
                                </option>
                            ))}
                        </SelectInput>

                        {/* DNI Field */}
                        <TextInput
                            label={t('enrollmentCertificate.dniLabel')}
                            name="dni"
                            value={data.dni}
                            onChange={(v) => setData('dni', v)}
                            placeholder={t(
                                'enrollmentCertificate.dniPlaceholder'
                            )}
                            error={errors.dni || serverErrors?.dni}
                        />

                        {/* Address Field */}
                        <TextInput
                            label={t('enrollmentCertificate.addressLabel')}
                            name="address"
                            value={data.address}
                            onChange={(v) => setData('address', v)}
                            placeholder={t(
                                'enrollmentCertificate.addressPlaceholder'
                            )}
                            error={errors.address || serverErrors?.address}
                        />

                        {/* Submit Button */}
                        <PrimaryButton
                            type="submit"
                            size="md"
                            disabled={!data.course_id}
                        >
                            {t('enrollmentCertificate.submitButton')}
                        </PrimaryButton>
                    </form>
                </div>
            </div>
        </AppLayout>
    );
};

const EnrollmentCertificate: React.FC<EnrollmentCertificateProps> = (props) => (
    <ThemeWrapper>
        <EnrollmentCertificateContent {...props} />
    </ThemeWrapper>
);

export default EnrollmentCertificate;
